import React, { useState } from 'react';

import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import {
  faCircleExclamation,
  faSpinnerThird,
} from '@awesome.me/kit-a6e51d954f/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Honeybadger from '@honeybadger-io/js';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert } from '../../../../../shared/components/Alert';
import { ERROR_TITLES, SUCCESS_MESSAGES } from '../../constants';
import { useCalendar, useDialogs } from '../../hooks';
import { deleteAppointment as apiDeleteAppointment } from '../../services/appointmentService';

const DeleteButton = ({ children, isLoading, onClick }) => {
  if (isLoading) {
    return (
      <AlertDialogAction disabled>
        <FontAwesomeIcon className="animate-spin" icon={faSpinnerThird} />
        Please wait
      </AlertDialogAction>
    );
  }

  return <AlertDialogAction onClick={onClick}>{children}</AlertDialogAction>;
};

DeleteButton.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const DeleteAppointmentDialog = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { deleteAppointment: removeFromCalendar } = useCalendar();

  const {
    activeAppointment,
    showDeleteAppointmentDialog: isOpen,
    handleCloseDeleteAppointmentDialog: onClose,
  } = useDialogs();

  const deleteAppointment = async () => {
    const appointmentId = activeAppointment.id;
    await apiDeleteAppointment(appointmentId);
    removeFromCalendar(appointmentId);
  };

  const handleSuccess = () => {
    onClose();
    toast.success(SUCCESS_MESSAGES.appointment.delete);
  };

  const handleDelete = async (e) => {
    // <AlertDialogAction /> closes the dialog on click, without waiting for
    // handleDelete() to finish execution. e.preventDefault() prevents this behavior.
    e.preventDefault();

    try {
      setErrorMessage('');
      setIsLoading(true);
      await deleteAppointment();
      handleSuccess();
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
      setErrorMessage('');
    }
  };

  return (
    <AlertDialog onOpenChange={handleOpenChange} open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>

          <AlertDialogDescription>
            Deleting this appointment will also remove the appointment from your Google
            Calendar.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!isEmpty(errorMessage) && (
          <Alert
            description={errorMessage}
            icon={<FontAwesomeIcon icon={faCircleExclamation} />}
            title={ERROR_TITLES.appointment.delete}
            variant="destructive"
          />
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>

          <DeleteButton isLoading={isLoading} onClick={handleDelete}>
            Delete
          </DeleteButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteAppointmentDialog;
